<template>
  <div class="my-4 rounded-lg bg-white border border-nera-border overflow-hidden shadow-sm">
    <header class="flex items-center justify-between gap-3 px-4 py-3 bg-nera-elevated-2 border-b border-nera-border">
      <h4 class="m-0 text-base font-semibold text-nera-text truncate">{{ chartData?.title || 'Chart' }}</h4>
      <div class="flex items-center gap-1 shrink-0">
        <button
          @click="handleSave"
          title="Save chart as PNG"
          aria-label="Save chart"
          class="inline-flex items-center justify-center w-8 h-8 rounded-md text-nera-muted bg-transparent cursor-pointer transition-colors hover:bg-nera-light-hover hover:text-nera-accent"
        >
          <i class="fas fa-download text-sm"></i>
        </button>
        <button
          @click="toggleMinimize"
          :title="isMinimized ? 'Expand chart' : 'Collapse chart'"
          :aria-label="isMinimized ? 'Expand chart' : 'Collapse chart'"
          class="inline-flex items-center justify-center w-8 h-8 rounded-md text-nera-muted bg-transparent cursor-pointer transition-colors hover:bg-nera-light-hover hover:text-nera-accent"
        >
          <i :class="['fas text-sm', isMinimized ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
        </button>
      </div>
    </header>

    <div v-show="!isMinimized" class="relative p-4 h-[300px] md:h-[400px] bg-white">
      <canvas ref="chartCanvas"></canvas>
    </div>

    <div v-if="isMinimized" class="px-4 py-3 text-center text-nera-muted text-sm bg-nera-elevated-2/50">
      Chart collapsed — click <i class="fas fa-chevron-down mx-1"></i> to expand.
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted, computed } from 'vue'
import Chart from 'chart.js/auto'

const props = defineProps({
  chartId: {
    type: String,
    required: true
  },
  chartPaths: {
    type: Array,
    required: true
  }
})

const chartPath = computed(() => {
  const chart = props.chartPaths.find(c => c[props.chartId])
  return chart ? chart[props.chartId] : null
})
const chartCanvas = ref(null)
const chartData = ref(null)
const chartInstance = ref(null)
const isMinimized = ref(false)

onMounted(async () => {
  await loadChartData()
})

watch(() => chartPath.value, async () => {
  await loadChartData()
})

onUnmounted(() => {
  if (chartInstance.value) {
    chartInstance.value.destroy()
  }
})

const loadChartData = async () => {
  if (!chartPath.value) return

  try {
    const response = await fetch(`/api/charts/${encodeURIComponent(chartPath.value)}`)
    const data = await response.json()

    chartData.value = data
    renderChart(data)
  } catch (error) {
    console.error('Failed to load chart:', error)
  }
}

const renderChart = (data) => {
  if (!chartCanvas.value || !data) return

  if (chartInstance.value) {
    chartInstance.value.destroy()
  }

  const ctx = chartCanvas.value.getContext('2d')
  const config = transformChartData(data)

  chartInstance.value = new Chart(ctx, config)
}

// Brand-aligned palette — used in order; cycles for additional series/categories.
const BRAND_PALETTE = [
  '#004aa9', // nera-accent
  '#3375d6', // user-msg blue
  '#48c774', // success green
  '#2862bf', // accent-hover
  '#6ba4e0', // soft blue
  '#1f5fa0', // deep blue
  '#94a3b8', // slate
]
const colorAt = (i) => BRAND_PALETTE[i % BRAND_PALETTE.length]

const TICK_COLOR = '#475569'    // nera-muted
const GRID_COLOR = '#e2e8f0'    // nera-border
const LABEL_COLOR = '#334155'   // light-text-strong
const FONT_FAMILY = 'inherit'

const transformChartData = (drawData) => {
  const { chart_type, subtitle, axis, data, options } = drawData
  const isCategorical = ['bar', 'pie', 'doughnut', 'polarArea'].includes(chart_type)
  const singleSeries = data.datasets.length === 1

  return {
    type: chart_type,
    data: {
      labels: data.labels,
      datasets: data.datasets.map((ds, i) => {
        // Single bar/pie series: color each datapoint distinctly so categories read.
        // Multi-series or line: one color per dataset.
        const perPointColors = isCategorical && singleSeries
          ? ds.data.map((_, j) => colorAt(j))
          : colorAt(i)

        return {
          label: ds.label,
          data: ds.data,
          backgroundColor: perPointColors,
          borderColor: chart_type === 'line' ? colorAt(i) : perPointColors,
          borderWidth: chart_type === 'line' ? 2 : 1,
          borderRadius: chart_type === 'bar' ? 4 : undefined,
          fill: chart_type === 'line' ? false : undefined,
          tension: chart_type === 'line' ? 0.4 : undefined,
          pointBackgroundColor: chart_type === 'line' ? colorAt(i) : undefined,
          pointBorderColor: chart_type === 'line' ? '#ffffff' : undefined,
          pointBorderWidth: chart_type === 'line' ? 2 : undefined,
          pointRadius: chart_type === 'line' ? 4 : undefined,
        }
      })
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: 4 },
      plugins: {
        // Title is rendered by the component header — disable chart.js's own.
        title: { display: false },
        subtitle: {
          display: !!subtitle,
          text: subtitle,
          color: LABEL_COLOR,
          font: { family: FONT_FAMILY, size: 13 },
          padding: { bottom: 12 },
        },
        legend: {
          display: options.show_legend,
          position: 'bottom',
          labels: {
            color: LABEL_COLOR,
            font: { family: FONT_FAMILY, size: 12 },
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            padding: 16,
          },
        },
        tooltip: {
          backgroundColor: '#1f2937',
          titleColor: '#ffffff',
          bodyColor: '#e2e8f0',
          borderColor: '#334155',
          borderWidth: 1,
          padding: 10,
          cornerRadius: 6,
          titleFont: { family: FONT_FAMILY, size: 12, weight: '600' },
          bodyFont: { family: FONT_FAMILY, size: 12 },
          boxPadding: 4,
          usePointStyle: true,
        },
      },
      scales: {
        x: {
          title: {
            display: !!axis.x_label,
            text: axis.x_label,
            color: LABEL_COLOR,
            font: { family: FONT_FAMILY, size: 12, weight: '500' },
          },
          ticks: {
            color: TICK_COLOR,
            font: { family: FONT_FAMILY, size: 11 },
          },
          grid: {
            display: options.show_grid,
            color: GRID_COLOR,
            drawBorder: false,
          },
          border: { color: GRID_COLOR },
        },
        y: {
          title: {
            display: !!axis.y_label,
            text: axis.y_label,
            color: LABEL_COLOR,
            font: { family: FONT_FAMILY, size: 12, weight: '500' },
          },
          ticks: {
            color: TICK_COLOR,
            font: { family: FONT_FAMILY, size: 11 },
          },
          grid: {
            display: options.show_grid,
            color: GRID_COLOR,
            drawBorder: false,
          },
          border: { color: GRID_COLOR },
          stacked: options.stacked || false,
          beginAtZero: true,
        },
      },
    },
  }
}

const toggleMinimize = () => {
  isMinimized.value = !isMinimized.value
}

const handleSave = () => {
  if (!chartCanvas.value) return

  const url = chartCanvas.value.toDataURL('image/png')
  const link = document.createElement('a')
  link.download = `${chartData.value?.title || 'chart'}.png`
  link.href = url
  link.click()
}
</script>
